import { useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import MuiFormHelperText from '@mui/material/FormHelperText'
import { styled } from '@mui/material/styles'
import MUITextField from '@mui/material/TextField'

import { DefaultElementProps } from 'common/interfaces'
import { SizingContainer } from 'components/SizingContainer'
import validationsToRules from 'utils/validationsToRules'

import { ReCaptcha as ReCaptchaProps } from './interface'

declare global {
  interface Window {
    grecaptcha: any // Not a valid Window prop -- loaded into globals by the Google ReCaptcha script
  }
}

const ReCaptchaRoot = styled('div')({
  width: '100%',
  minHeight: '78px',
  '& > div': {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
})

const ReCaptchaValidationWrapper = styled('div')({
  width: '300px',
  marginLeft: 'auto',
  marginRight: 'auto',
})

export default function ReCaptcha({
  name,
  validations,
}: ReCaptchaProps & DefaultElementProps): JSX.Element {
  const [mountPointId] = useState(`${name}-recaptcha`)
  const grecaptchaRef = useRef<any>(null)
  const { control, setValue } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: '',
    rules: validationsToRules(validations),
  })

  const handleReCaptchaExpired = (): void => {
    setValue(name, '')
  }

  const handleReCaptchaChange = (token: any): void => {
    setValue(name, token)
  }

  const handleReCaptchaError = (e: any): void => {
    // console.log(e); // Is there anything to do with this error?
    setValue(name, '')
  }

  const handleReCaptchaLoaded = (): void => {
    grecaptchaRef.current = window.grecaptcha
    window.grecaptcha.ready(() => {
      grecaptchaRef.current?.render(mountPointId, {
        sitekey: process.env.GOOGLE_RECAPTCHA_SITE_KEY!,
        'expired-callback': handleReCaptchaExpired,
        'error-callback': handleReCaptchaError,
        callback: handleReCaptchaChange,
      })
    })
  }

  // Add ReCaptcha to page
  useEffect(() => {
    setValue(name, '') // If the user exists the page, they to re-verify (Not really useful now, but for when we implement token verification this will be needed.)
    if (grecaptchaRef.current) return // Don't want to load this twice if it already exists in globals
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit` // `render=explicit` Allows us when to choose to render (handleReCaptchaLoaded above)
    script.async = true
    script.defer = true
    document.body.appendChild(script)
    script.onload = () => {
      handleReCaptchaLoaded()
    }

    return () => {
      script.remove()
    }
  }, [])

  const fieldProps = {
    ...field,
    inputRef: field.ref,
  } as const

  return (
    <SizingContainer className="text-field-wrap">
      <ReCaptchaRoot className="g-recaptcha" id={mountPointId} />
      <MUITextField
        required={validations.required?.value}
        fullWidth
        type="hidden"
        style={{ display: 'none' }}
        {...fieldProps}
      />
      <ReCaptchaValidationWrapper>
        {error ? <MuiFormHelperText error>{error.message}</MuiFormHelperText> : null}
      </ReCaptchaValidationWrapper>
    </SizingContainer>
  )
}
